/**
 * @author: lv.chen
 * @date: 2025/4/29
 * @desc: CustomizeTagsInputPanel自定义标签输入面板组件
 */
import React, { useState } from 'react';
import classnames from 'classnames';
import { Button } from 'tea-component';
import CustomizeTagInput, { ICustomizeTagInputValidation } from '@gp/components/CustomizeTagInput';
import useCustomizeTagInputAutoFocus from './_hooks/useCustomizeTagInputAutoFocus';
import { formatTagsNames } from '../../utils';
import { convert2SelectOptionValue } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { ICustomizeTagInputValue } from '@gp/components/CustomizeTagInput/types';

import './index.scss';

interface ICustomizeTagsInputPanelProps extends IBaseProps {
  ignoreCase?: boolean;
  invalidCustomizeTagChars?: string[];
  invalidCustomizeTagCharsMsg?: string;
  placeholder?: string;
  existedOptions?: IGPSelectOption[] | null;
  generateCustomizeAddOptionValue?: (text?: string) => string;
  generateCustomizeAddOptionData?: (value?: string, text?: string) => unknown;
  onBack?: () => void;
  onCustomizeAdd?: (payload: IGPSelectOption[]) => void;
}

const CustomizeTagsInputPanel: React.FC<ICustomizeTagsInputPanelProps> = (props) => {
  const {
    style,
    className,
    ignoreCase,
    invalidCustomizeTagChars,
    invalidCustomizeTagCharsMsg,
    placeholder,
    existedOptions,
    generateCustomizeAddOptionValue,
    generateCustomizeAddOptionData,
    onBack,
    onCustomizeAdd,
  } = props;

  const existedTags = formatTagsNames(existedOptions);

  const customizeTagInput = useCustomizeTagInputAutoFocus();

  const [tagInputValue, setTagInputValue] = useState<ICustomizeTagInputValue>({ tags: [], input: '' });

  const validateCustomizeTag = (tag?: string): ICustomizeTagInputValidation => {
    if (!tag) return { invalid: false, message: '' };
    const invalidCustomizeTagCharsTmp = !ignoreCase
      ? invalidCustomizeTagChars
      : invalidCustomizeTagChars?.map((item) => item?.toLowerCase());
    const tagTmp = !ignoreCase ? tag : tag?.toLowerCase();
    const hasInvalidChars = invalidCustomizeTagCharsTmp?.some((char) => tagTmp?.includes(char));
    return !hasInvalidChars
      ? { invalid: false, message: '' }
      : { invalid: true, message: invalidCustomizeTagCharsMsg || 'invalid' };
  };

  const handleTagInputValueChange = (payload: ICustomizeTagInputValue) => {
    const tagInputValueTmp = { ...tagInputValue, ...payload };
    setTagInputValue(tagInputValueTmp);
  };

  const handleTagInputValueConfirm = () => {
    const valueTmp = convert2SelectOptionValue(tagInputValue, {
      ignoreCase,
      existedTags,
      validateCustomizeTag,
      generateCustomizeAddOptionValue,
      generateCustomizeAddOptionData,
    });
    if (Array.isArray(valueTmp)) {
      setTagInputValue({ tags: [], input: '' });
      onCustomizeAdd?.(valueTmp);
    }
  };

  return (
    <div style={style} className={classnames('dbg-customize-tag-select-dropdown-customize-panel', className)}>
      <div className="dbg-customize-tag-select-dropdown-customize-panel__body">
        <CustomizeTagInput
          ref={customizeTagInput}
          ignoreCase={ignoreCase}
          placeholder={placeholder}
          existedTags={existedTags}
          validateTag={validateCustomizeTag}
          value={tagInputValue}
          onChange={handleTagInputValueChange}
        />
      </div>
      <div className="dbg-customize-tag-select-dropdown-customize-panel__footer">
        <Button
          type="primary"
          disabled={!tagInputValue?.tags?.length && !tagInputValue?.input?.trim()}
          onClick={(e) => {
            e?.stopPropagation();
            handleTagInputValueConfirm();
          }}
        >
          Confirm
        </Button>
        <Button
          onClick={(e) => {
            e?.stopPropagation();
            onBack?.();
          }}
        >
          Back
        </Button>
      </div>
    </div>
  );
};

export default CustomizeTagsInputPanel;
